import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ClockIcon, FlameIcon } from 'lucide-react';
const COUNTDOWN_HOURS = 24;
const STORAGE_KEY = 'me_offer_deadline_v1';
function getDeadline(): number {
  if (typeof window === 'undefined') return Date.now() + COUNTDOWN_HOURS * 3600 * 1000;
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored) {
    const ts = parseInt(stored, 10);
    if (!isNaN(ts) && ts > Date.now()) return ts;
  }
  // Set a fresh 24h deadline anchored to the user's first visit
  const fresh = Date.now() + COUNTDOWN_HOURS * 3600 * 1000;
  window.localStorage.setItem(STORAGE_KEY, String(fresh));
  return fresh;
}
function pad(n: number) {
  return n < 10 ? `0${n}` : String(n);
}
export function UrgencyBanner() {
  const [deadline] = useState<number>(() => getDeadline());
  const [now, setNow] = useState<number>(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const remainingMs = Math.max(0, deadline - now);
  const totalMs = COUNTDOWN_HOURS * 3600 * 1000;
  const elapsedPercent = Math.min(100, (totalMs - remainingMs) / totalMs * 100);
  const totalSec = Math.floor(remainingMs / 1000);
  const hours = Math.floor(totalSec / 3600);
  const minutes = Math.floor(totalSec % 3600 / 60);
  const seconds = totalSec % 60;
  return <motion.div initial={{
    opacity: 0,
    y: -10
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} transition={{
    duration: 0.5
  }} className="max-w-3xl mx-auto mb-5 md:mb-7" data-id="element-321">
      <div className="relative overflow-hidden rounded-md border-2 border-gold/60 bg-plum-dark shadow-xl shadow-plum/30" data-id="element-322">
        {/* Animated shimmer accent */}
        <div className="absolute inset-0 pointer-events-none opacity-20" data-id="element-323">
          <motion.div className="absolute inset-y-0 w-1/3 bg-gradient-to-r from-transparent via-gold/40 to-transparent" animate={{
          x: ['-100%', '400%']
        }} transition={{
          duration: 3.5,
          repeat: Infinity,
          ease: 'linear'
        }} data-id="element-324" />
        </div>

        <div className="relative px-3 py-2.5 md:px-5 md:py-3" data-id="element-325">
          {/* Top row: badge + timer */}
          <div className="flex items-center justify-between gap-2 md:gap-4" data-id="element-326">
            {/* Left: pulsing badge */}
            <div className="flex items-center gap-1.5 md:gap-2 flex-shrink-0" data-id="element-327">
              <div className="relative flex items-center justify-center" data-id="element-328">
                <motion.div className="absolute inset-0 rounded-full bg-red-500" animate={{
                scale: [1, 1.6, 1],
                opacity: [0.6, 0, 0.6]
              }} transition={{
                duration: 1.5,
                repeat: Infinity
              }} data-id="element-329" />
                <div className="relative w-2 h-2 md:w-2.5 md:h-2.5 rounded-full bg-red-500" data-id="element-330" />
              </div>
              <FlameIcon size={14} className="text-gold md:hidden" data-id="element-331" />
              <span className="text-[9px] md:text-[11px] font-bold uppercase tracking-[0.15em] md:tracking-[0.2em] text-gold whitespace-nowrap" data-id="element-332">
                Oferta termina en
              </span>
            </div>

            {/* Center: countdown digits */}
            <div className="flex items-center gap-1 md:gap-1.5 flex-shrink-0" data-id="element-333">
              <TimeUnit value={pad(hours)} label="h" data-id="element-334" />
              <span className="text-gold font-bold text-base md:text-xl pb-1" data-id="element-335">
                :
              </span>
              <TimeUnit value={pad(minutes)} label="m" data-id="element-336" />
              <span className="text-gold font-bold text-base md:text-xl pb-1" data-id="element-337">
                :
              </span>
              <TimeUnit value={pad(seconds)} label="s" pulse data-id="element-338" />
            </div>

            {/* Right (desktop only): clock icon */}
            <div className="hidden md:flex items-center text-gold/60 flex-shrink-0" data-id="element-339">
              <ClockIcon size={18} data-id="element-340" />
            </div>
          </div>

          {/* Progress bar */}
          <div className="mt-2 md:mt-2.5 h-1 md:h-1.5 w-full bg-plum/60 rounded-full overflow-hidden" data-id="element-341">
            <motion.div className="h-full bg-gradient-to-r from-red-500 via-gold to-gold-light" initial={false} animate={{
            width: `${elapsedPercent}%`
          }} transition={{
            duration: 0.6,
            ease: 'easeOut'
          }} data-id="element-342" />
          </div>

          {/* Bottom message */}
          <p className="mt-2 text-[10px] md:text-[12px] text-cream/85 leading-tight text-center font-medium" data-id="element-343">
            Cuando se acaben los cupos, el precio sube{' '}
            <span className="text-gold font-bold" data-id="element-344">+$10.000</span>{' '}
            <span className="text-cream/60" data-id="element-345">y no vuelve a bajar.</span>
          </p>
        </div>
      </div>
    </motion.div>;
}
function TimeUnit({
  value,
  label,
  pulse
}: {
  value: string;
  label: string;
  pulse?: boolean;
}) {
  return <div className="flex flex-col items-center" data-id="element-346">
      <motion.div key={value} initial={pulse ? {
      scale: 1.15,
      opacity: 0.7
    } : false} animate={{
      scale: 1,
      opacity: 1
    }} transition={{
      duration: 0.3
    }} className="bg-plum px-1.5 py-0.5 md:px-2 md:py-1 rounded border border-gold/30 min-w-[28px] md:min-w-[36px] text-center" data-id="element-347">
        <span className="font-serif font-bold text-base md:text-xl text-gold tabular-nums leading-none" data-id="element-348">
          {value}
        </span>
      </motion.div>
      <span className="text-[7px] md:text-[8px] uppercase tracking-widest text-cream/40 mt-0.5" data-id="element-349">
        {label}
      </span>
    </div>;
}